import type { DestinationOption } from '@/types/provider-destinations';

import type { WizardScopeSelection } from './useCreateRequestWizard';

export interface ThreadedRequestGroup {
  key: string;
  destination: DestinationOption | null;
  scopes: WizardScopeSelection[];
  followUpPhone: string;
}

const normalizeDestinationValue = (destination: DestinationOption): string => {
  const raw = destination.value?.trim() ?? '';
  if (!raw) return '';

  switch (destination.channel_type) {
    case 'FAX':
      return raw.replace(/\D/g, '');
    case 'EMAIL':
      return raw.toLowerCase();
    case 'MAIL':
      return raw.toLowerCase().replace(/\s+/g, ' ');
    case 'PORTAL':
      return raw.toLowerCase();
    default:
      return raw.toLowerCase();
  }
};

export const getThreadGroupKey = (destination: DestinationOption): string => {
  const normalizedValue = normalizeDestinationValue(destination);

  if (normalizedValue) {
    return `${destination.channel_type}:${normalizedValue}`;
  }

  const contactPointId = destination.contact_point_id
    ? String(destination.contact_point_id)
    : '';

  if (contactPointId) {
    return `contact:${contactPointId}`;
  }

  return `manual:${destination.channel_type}`;
};

const normalizeFollowUpPhone = (phone: string): string =>
  phone.trim().replace(/\D/g, '');

export const groupSelectedScopesByDestination = (
  selectedScopes: WizardScopeSelection[],
): ThreadedRequestGroup[] => {
  const groups = new Map<string, ThreadedRequestGroup>();

  selectedScopes.forEach((selection) => {
    if (!selection.destination && !selection.createWithoutDestination) return;

    const followUpPhone = selection.destination
      ? selection.followUpPhone.trim()
      : '';
    const key = selection.destination
      ? `${getThreadGroupKey(selection.destination)}:voice:${normalizeFollowUpPhone(followUpPhone)}`
      : `placeholder:${selection.scope}`;
    const existing = groups.get(key);

    if (existing) {
      existing.scopes.push(selection);
      return;
    }

    groups.set(key, {
      key,
      destination: selection.destination,
      scopes: [selection],
      followUpPhone,
    });
  });

  return Array.from(groups.values());
};

export const getThreadedRequestCount = (
  selectedScopes: WizardScopeSelection[],
): number => groupSelectedScopesByDestination(selectedScopes).length;
